import React, { useEffect, useState } from 'react';
import { fetchArxivFeeds, SearchParams } from '../api/arxiv';
import { fetchCrossRefFeeds } from '../api/crossref';
import { ArticleDetail } from './ArticleDetail';
import '../styles/feed.css';

interface Paper {
  title: string;
  authors: string[];
  summary: string;
  published: string;
  link: string;
}

interface RSSFeedProps {
  searchParams?: SearchParams;
  source: 'arxiv' | 'crossref';
}

export const RSSFeed: React.FC<RSSFeedProps> = ({ searchParams = {}, source }) => {
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPaper, setSelectedPaper] = useState<Paper | null>(null);

  const fetchPapers = async (params?: SearchParams) => {
    setLoading(true);
    setError(null);
    try {
      const data = source === 'arxiv'
        ? await fetchArxivFeeds(params)
        : await fetchCrossRefFeeds(params);
      setPapers(data);
    } catch (err) {
      setError('Failed to fetch papers. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPapers(searchParams);
  }, [searchParams, source]);

  const handlePaperClick = (paper: Paper) => {
    setSelectedPaper(paper);
  };

  const handleCloseDetail = () => {
    setSelectedPaper(null);
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading && papers.length === 0) {
    return <div className="loading">⏳ Loading papers...</div>;
  }

  if (error && papers.length === 0) {
    return <div className="error">❌ {error}</div>;
  }

  return (
    <div className="feed-container">
      <div className="feed-header">
        <h2>
          {searchParams.keywords || searchParams.yearFrom || searchParams.yearTo
            ? `Search Results from ${source === 'arxiv' ? 'ArXiv' : 'CrossRef'}`
            : `Latest Research Papers from ${source === 'arxiv' ? 'ArXiv' : 'CrossRef'}`
          }
        </h2>
        <p className="feed-description">
          {searchParams.keywords || searchParams.yearFrom || searchParams.yearTo
            ? `Showing results for ${searchParams.keywords ? `keywords: "${searchParams.keywords}"` : ''}${searchParams.yearFrom || searchParams.yearTo ? ` ${searchParams.yearFrom ? `from ${searchParams.yearFrom}` : ''}${searchParams.yearTo ? ` to ${searchParams.yearTo}` : ''}` : ''}`
            : 'Discover the most recent research across multiple disciplines'
          }
        </p>
      </div>

      {error && papers.length > 0 && (
        <div className="error-banner">⚠️ {error}</div>
      )}

      <div className="papers-list">
        {papers.map((paper, index) => (
          <article key={index} className="paper-card" onClick={() => handlePaperClick(paper)}>
            <h2 className="paper-title">{paper.title}</h2>
            <p className="authors">👥 <strong>Authors:</strong> {paper.authors.join(', ')}</p>
            <p className="summary">{paper.summary.substring(0, 300)}{paper.summary.length > 300 ? '...' : ''}</p>
            <div className="paper-meta">
              <span className="date">📅 {formatDate(paper.published)}</span>
              <button className="read-more" onClick={(e) => {
                e.stopPropagation();
                window.open(paper.link, '_blank');
              }}>
                Read on {source === 'arxiv' ? 'ArXiv' : 'CrossRef'} →
              </button>
            </div>
          </article>
        ))}
      </div>

      {selectedPaper && (
        <ArticleDetail paper={selectedPaper} onClose={handleCloseDetail} source={source} />
      )}
    </div>
  );
};